/* eslint-disable */
/* ---------- FLEET OPS — live animated fleet topology (v2) ----------
   Exports: FleetOps
   The homepage centerpiece: an operator's board showing how work flows
   through the fleet in real time.

     inputs (8 channels) ─┐
                          ├→ router ─→ TIER 1 · always-on  ┌ HERMES (the monitor)
     grok x-scouts ───────┘            │                   └ DIRECT CLI (claude code · codex)
                                       └→ TIER 2 · breadth ── OPENCLAW (breadth)
                                                  ↓
                                       outputs (posts/replies/reports/deploys/commits/alerts)

   v2 additions:
   - DIRECT CLI tier box — the CLI agents (Claude Code + Codex sessions)
     promoted out of the Hermes box; they ship deploys + commits.
   - GROK X-SCOUT loop (magenta) — grok agents watch the live X timeline,
     distill trends, feed the router. Packets run 4-leg journeys:
     x → grok → router → breadth → shipped post.
   - "tier 1 · always-on" dashed grouping around Hermes + CLI keeps the
     canonical two-tier story intact.
   - RESTART demo: a worker group stalls, Hermes detects it and
     restarts the workers. ?replay=1 auto-fires the
     restart sequence once the board is in view (clean screen recordings).

   Canonical: 8 input channels.

   prefers-reduced-motion: static diagram, no packets, no replay.
   Mobile: board pans horizontally inside the window frame (body
   scrollWidth stays == innerWidth).
   ------------------------------------------------------------------ */

import React, { useEffect, useMemo, useRef, useState } from 'react';

const SVG_NS = "http://www.w3.org/2000/svg";

function foReducedMotion() {
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function usePrefersReducedMotionLocal() {
  const [reduced, setReduced] = useState(foReducedMotion);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return reduced;
}

function injectFleetOpsStyles() {
  if (typeof document === "undefined" || document.getElementById("kz-fleet-ops-styles")) return;
  const s = document.createElement("style");
  s.id = "kz-fleet-ops-styles";
  s.textContent = `
    .fo-scroll{ overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .fo-scroll svg{ display: block; width: 100%; height: auto; }
    @media (max-width: 920px){
      .fo-scroll svg{ min-width: 880px; }
    }
    .fo-drag-hint{ display: none; }
    @media (max-width: 920px){
      .fo-drag-hint{
        display: block; text-align: center; padding: 6px 0 2px;
        font-size: 9px; color: var(--fg-4); letter-spacing: 0.22em; text-transform: uppercase;
      }
    }

    /* lanes — faint circuit traces with a slow dash drift */
    .fo-lane{
      fill: none; stroke: var(--line-strong);
      stroke-width: 1; opacity: 0.55;
    }
    .fo-lane-flow{
      fill: none; stroke-width: 1;
      stroke-dasharray: 2 7;
      opacity: 0.35;
      animation: foDashDrift 1.4s linear infinite;
    }
    .fo-lane-flow.fo-c-hermes{ stroke: #5FBFB9; }
    .fo-lane-flow.fo-c-breadth{ stroke: #F5A623; }
    .fo-lane-flow.fo-c-cli{ stroke: var(--fg-2); }
    .fo-lane-flow.fo-c-out{ stroke: var(--green); }
    .fo-lane-flow.fo-c-in{ stroke: var(--fg-4); }
    .fo-lane-flow.fo-c-grok{ stroke: var(--magenta, #c98ec0); opacity: 0.5; }
    @keyframes foDashDrift{ to{ stroke-dashoffset: -9; } }

    /* packets */
    .fo-pk{ pointer-events: none; }
    .fo-pk-in{ fill: var(--fg-2); filter: drop-shadow(0 0 3px rgba(232,230,220,0.7)); }
    .fo-pk-hermes{ fill: #5FBFB9; filter: drop-shadow(0 0 4px rgba(95,191,185,0.9)); }
    .fo-pk-breadth{ fill: #F5A623; filter: drop-shadow(0 0 4px rgba(245,166,35,0.8)); }
    .fo-pk-cli{ fill: var(--fg); filter: drop-shadow(0 0 4px rgba(232,230,220,0.9)); }
    .fo-pk-out{ fill: var(--green); filter: drop-shadow(0 0 4px rgba(0,220,130,0.85)); }
    .fo-pk-alert{ fill: var(--red); filter: drop-shadow(0 0 5px rgba(213,101,101,0.95)); }
    .fo-pk-grok{ fill: var(--magenta, #c98ec0); filter: drop-shadow(0 0 5px rgba(201,142,192,0.95)); }

    /* nodes */
    .fo-node{ fill: var(--bg-1); stroke: var(--line-strong); stroke-width: 1; }
    .fo-box{ fill: var(--bg-1); stroke: var(--line-strong); stroke-width: 1; }
    .fo-hermes-box{ stroke: rgba(95,191,185,0.55); }
    .fo-cli-box{ stroke: rgba(232,230,220,0.35); }
    .fo-oc-box{ stroke: rgba(245,166,35,0.45); }
    .fo-grok-box{ stroke: rgba(201,142,192,0.5); }
    .fo-tier-group{
      fill: none; stroke: rgba(95,191,185,0.22);
      stroke-width: 1; stroke-dasharray: 4 6;
    }
    .fo-t-title{ font-family: var(--mono); font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; fill: var(--fg-3); }
    .fo-t-node{ font-family: var(--mono); font-size: 10px; fill: var(--fg-2); }
    .fo-t-cap{ font-family: var(--mono); font-size: 8.5px; letter-spacing: 0.12em; text-transform: uppercase; fill: var(--fg-4); }
    .fo-t-accent-h{ fill: #5FBFB9; }
    .fo-t-accent-o{ fill: #F5A623; }
    .fo-t-accent-g{ fill: var(--magenta, #c98ec0); }

    /* hermes profile squares — slow phosphor breathing, staggered */
    .fo-hsq{ fill: rgba(95,191,185,0.16); stroke: #5FBFB9; stroke-width: 1; }
    .fo-hsq-core{ animation: foBreath 2.8s ease-in-out infinite; animation-delay: calc(var(--i) * 0.35s); }
    @keyframes foBreath{
      0%, 100%{ fill: rgba(95,191,185,0.14); }
      50%{ fill: rgba(95,191,185,0.45); }
    }
    /* cli session squares — white phosphor breathing */
    .fo-csq{
      fill: rgba(232,230,220,0.10); stroke: var(--fg-3); stroke-width: 1;
      animation: foCliBreath 3.1s ease-in-out infinite;
      animation-delay: calc(var(--i) * 0.45s);
    }
    @keyframes foCliBreath{
      0%, 100%{ fill: rgba(232,230,220,0.08); }
      50%{ fill: rgba(232,230,220,0.38); }
    }
    /* grok scout diamonds — magenta radar pulse */
    .fo-gsq{
      fill: rgba(201,142,192,0.16); stroke: var(--magenta, #c98ec0); stroke-width: 1;
      animation: foGrokPulse 2.2s ease-in-out infinite;
      animation-delay: calc(var(--i) * 0.55s);
    }
    @keyframes foGrokPulse{
      0%, 100%{ fill: rgba(201,142,192,0.12); }
      50%{ fill: rgba(201,142,192,0.5); }
    }

    /* openclaw dots — alive shimmer in normal ops */
    .fo-dot{
      fill: rgba(245,166,35,0.55); stroke: none;
      animation: foShimmer 3.4s ease-in-out infinite;
      animation-delay: calc(var(--i) * 0.09s);
    }
    @keyframes foShimmer{
      0%, 100%{ fill: rgba(245,166,35,0.30); }
      50%{ fill: rgba(245,166,35,0.75); }
    }

    /* ── restart phases ─────────────────────────────────────
       A single worker group (one shelf of dots) stalls. Hermes
       detects it and sweeps teal across the group, then the workers
       relight green→amber. Only the stalled cluster is ever affected —
       the rest of the fleet keeps shimmering. Nothing goes dark. */
    .fo-phase-blip .fo-dot--stall{
      animation: foStall 600ms ease-in-out forwards;
      animation-delay: calc(var(--si) * 26ms);
    }
    @keyframes foStall{
      0%{ fill: rgba(245,166,35,0.55); }
      45%{ fill: var(--red); }
      100%{ fill: rgba(213,101,101,0.4); }
    }
    .fo-phase-healing .fo-dot--stall{
      animation: foHealSweep 760ms ease-in-out infinite;
      animation-delay: calc(var(--si) * 22ms);
    }
    @keyframes foHealSweep{
      0%, 100%{ fill: rgba(213,101,101,0.35); }
      50%{ fill: rgba(95,191,185,0.7); }
    }
    .fo-phase-healed .fo-dot--stall{
      animation: foRelight 460ms ease-out forwards;
      animation-delay: calc(var(--si) * 24ms);
    }
    @keyframes foRelight{
      0%{ fill: rgba(95,191,185,0.7); }
      55%{ fill: var(--green); }
      100%{ fill: rgba(245,166,35,0.55); }
    }
    /* hermes is doing the work → it glows through blip + heal */
    .fo-phase-blip .fo-hermes-box,
    .fo-phase-healing .fo-hermes-box{
      stroke: #5FBFB9;
      filter: drop-shadow(0 0 10px rgba(95,191,185,0.7));
    }
    .fo-phase-healing .fo-oc-box{
      stroke: rgba(95,191,185,0.5);
    }

    /* watch rail — hermes monitors the breadth tier */
    .fo-watch{
      stroke: #5FBFB9; stroke-width: 1; stroke-dasharray: 3 5;
      opacity: 0.7; animation: foDashDrift 1s linear infinite;
    }
    .fo-phase-blip .fo-watch,
    .fo-phase-healing .fo-watch{
      opacity: 1; stroke-width: 1.5;
      filter: drop-shadow(0 0 5px rgba(95,191,185,0.9));
    }

    /* status line under the board */
    .fo-status{
      margin-top: 14px;
      font-family: var(--mono); font-size: 12px;
      color: var(--fg-2);
      min-height: 20px;
      letter-spacing: 0.02em;
    }
    .fo-status .fo-status-prompt{ color: var(--green); }
    .fo-status.fo-status-alert{ color: var(--red); }
    .fo-status.fo-status-principle{ color: #5FBFB9; }
    .fo-status .cursor{ vertical-align: -2px; }

    /* replay button (in win-title meta slot) */
    .fo-replay{
      font-family: var(--mono); font-size: 9px;
      letter-spacing: 0.18em; text-transform: uppercase;
      color: var(--fg-3);
      border: 1px solid var(--line-strong);
      background: transparent;
      padding: 3px 9px;
      cursor: pointer;
      transition: color 140ms ease, border-color 140ms ease, background 140ms ease;
      white-space: nowrap;
    }
    .fo-replay:hover{
      color: var(--red); border-color: var(--red);
      background: color-mix(in oklch, var(--red) 8%, transparent);
    }
    .fo-replay[disabled]{ opacity: 0.45; cursor: wait; }
    @media (max-width: 640px){
      .fo-replay{ font-size: 8px; padding: 2px 6px; letter-spacing: 0.1em; }
    }

    /* footer ticker */
    .fo-tick{
      display: inline-block;
      animation: foTickIn 320ms ease-out;
    }
    @keyframes foTickIn{
      from{ opacity: 0; transform: translateY(3px); }
      to{ opacity: 1; transform: none; }
    }

    .fo-legend{
      display: flex; flex-wrap: wrap; gap: 8px 18px;
      margin-top: 10px;
      font-size: 9px; color: var(--fg-4);
      letter-spacing: 0.16em; text-transform: uppercase;
    }
    .fo-legend i{
      display: inline-block; width: 7px; height: 7px;
      margin-right: 6px; vertical-align: middle; font-style: normal;
    }

    /* ReadyIQ off-ramp under the board */
    .fo-readyiq-cta{
      margin-top: 22px;
      border: 1px solid rgba(124,58,237,0.5);
      background: color-mix(in oklch, #7C3AED 5%, transparent);
      padding: 14px 18px;
      display: flex; flex-wrap: wrap; gap: 10px 18px;
      align-items: center; justify-content: space-between;
    }
    .fo-readyiq-cta .fo-rq-q{
      font-size: 13px; color: var(--fg-2);
    }
    .fo-readyiq-cta .fo-rq-q b{ color: #a78bfa; font-weight: 600; }
    .fo-readyiq-cta a{
      font-family: var(--mono); font-size: 11px;
      letter-spacing: 0.14em; text-transform: uppercase;
      color: #a78bfa; text-decoration: none;
      border: 1px solid rgba(124,58,237,0.6);
      padding: 8px 14px;
      transition: background 140ms ease, color 140ms ease, border-color 140ms ease;
      white-space: nowrap;
    }
    .fo-readyiq-cta a:hover{
      background: #7C3AED; color: #fff; border-color: #7C3AED;
    }

    @media (prefers-reduced-motion: reduce){
      .fo-lane-flow, .fo-watch, .fo-dot, .fo-hsq-core, .fo-csq, .fo-gsq,
      .fo-dot--stall{ animation: none !important; }
      .fo-dot{ fill: rgba(245,166,35,0.55); }
      .fo-replay{ display: none; }
      .fo-tick{ animation: none; }
    }
  `;
  document.head.appendChild(s);
}

/* ── geometry ──────────────────────────────────────────────────────
   viewBox 0 0 960 560. Inputs + grok loop left, router mid-left,
   tier-1 group (hermes + cli) and tier-2 (openclaw) right, outputs
   far right. All lane paths drawn source→destination so packets
   advance with increasing t. */
const IN_YS = [60, 102, 144, 186, 228, 270, 312, 354];
const OUT_YS = [70, 140, 210, 280, 350, 420];
const INPUTS = ["email", "slack", "whatsapp", "messenger", "telegram", "voice", "tickets", "ops"];
const OUTPUTS = ["posts", "replies", "reports", "deploys", "commits", "alerts"];
const ROUTER_IN_YS = [179, 195, 211, 227, 243, 259, 275, 291];

function buildLanes() {
  const lanes = {};
  INPUTS.forEach((_, i) => {
    const y = IN_YS[i], ry = ROUTER_IN_YS[i];
    lanes[`in${i}`] = { d: `M 142 ${y} C 192 ${y}, 192 ${ry}, 240 ${ry}`, cls: "fo-c-in" };
  });
  /* grok scout loop: x timeline → grok box → router */
  lanes.x2g = { d: "M 142 480 C 162 480, 170 480, 190 480", cls: "fo-c-grok" };
  lanes.g2r = { d: "M 300 440 C 300 392, 315 356, 315 310", cls: "fo-c-grok" };
  /* router → tiers */
  lanes.r2h = { d: "M 390 190 C 430 190, 430 96, 470 96", cls: "fo-c-hermes" };
  lanes.r2c = { d: "M 390 235 C 440 235, 452 233, 500 233", cls: "fo-c-cli" };
  lanes.r2o = { d: "M 390 280 C 432 280, 432 420, 470 420", cls: "fo-c-breadth", trunk: true };
  /* tiers → outputs */
  lanes.h_reports = { d: "M 740 96 C 784 96, 782 210, 822 210", cls: "fo-c-out" };
  lanes.h_alerts = { d: "M 740 96 C 800 96, 778 420, 822 420", cls: "fo-c-out" };
  lanes.c_deploys = { d: "M 740 233 C 784 233, 784 280, 822 280", cls: "fo-c-out" };
  lanes.c_commits = { d: "M 740 233 C 790 233, 780 350, 822 350", cls: "fo-c-out" };
  lanes.o_posts = { d: "M 740 420 C 788 420, 780 70, 822 70", cls: "fo-c-out" };
  lanes.o_replies = { d: "M 740 420 C 786 420, 780 140, 822 140", cls: "fo-c-out" };
  /* monitor rail: hermes → breadth tier, passing the tier-1 gutter */
  lanes.watch = { d: "M 480 150 L 480 316", cls: "", watch: true };
  return lanes;
}

const PHASE_MSG = {
  normal: "fleet online · 8 channels routing · grok watching x for trends",
  blip: "⚠ worker group stalled · hermes caught it in seconds",
  healing: "hermes auto-restarting the workers · no human in the loop",
  healed: "workers back online",
  post: "workers restarted · fleet online",
};

const TICK_LINES = [
  "route: draft → free tier · ok",
  "grok: x trend brief → router",
  "claude code: build green · ok",
  "health-check · fleet · ok",
  "codex: pr opened · review queued",
  "morning brief · delivered ✓",
  "billing: flat-rate subs",
  "deploy: kevinz.ai · vercel · ok",
];

/* fisher-yates with fixed seed — stable cascade order across renders */
function seededShuffle(n, seed) {
  const idx = Array.from({ length: n }, (_, i) => i);
  let s = seed;
  const rand = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  return idx;
}

function FleetBoard({ phase, registerPath, packetLayerRef }) {
  const lanes = useMemo(buildLanes, []);
  const dieOrder = useMemo(() => seededShuffle(48, 1337), []);
  const liteOrder = useMemo(() => seededShuffle(48, 4242), []);

  /* openclaw dot grid: 12 cols × 4 rows.
     Row index 1 (12 dots) is the "stall cluster" — the only group the
     restart sequence ever touches. The other rows keep shimmering. */
  const STALL_ROW = 1;
  const ocDots = useMemo(() => {
    const dots = [];
    let n = 0;
    for (let r = 0; r < 4 && n < 48; r++) {
      for (let c = 0; c < 12 && n < 48; c++) {
        dots.push({ x: 490 + c * 18, y: 352 + r * 18, i: n, row: r, col: c });
        n++;
      }
    }
    return dots;
  }, []);

  return (
    <svg
      viewBox="0 0 960 560"
      role="img"
      aria-label="Live fleet topology: eight input channels plus Grok agents scanning X for trends route through a model gateway into two tiers — the always-on tier (Hermes substrate, plus direct CLI agents running Claude Code and Codex) and the OpenClaw breadth tier — producing posts, replies, reports, deploys, commits and alerts."
      className={`fo-board fo-phase-${phase}`}
    >
      <title>kevinz.ai fleet ops — live topology</title>

      {/* lanes: base trace + animated flow overlay */}
      {Object.entries(lanes).map(([id, l]) => l.watch ? (
        <path key={id} d={l.d} className="fo-watch" ref={(el) => registerPath(id, el)} />
      ) : (
        <g key={id}>
          <path d={l.d} className="fo-lane" />
          <path
            d={l.d}
            className={`fo-lane-flow ${l.cls}${l.trunk ? " fo-lane-breadth-trunk" : ""}`}
            ref={(el) => registerPath(id, el)}
          />
        </g>
      ))}

      {/* ── inputs ── */}
      <text x="24" y="38" className="fo-t-title">inputs · 8 channels</text>
      {INPUTS.map((name, i) => (
        <g key={name}>
          <rect x="24" y={IN_YS[i] - 14} width="118" height="28" className="fo-node" shapeRendering="crispEdges" />
          <text x="38" y={IN_YS[i] + 3.5} className="fo-t-node">▸ {name}</text>
        </g>
      ))}

      {/* ── grok x-scout loop ── */}
      <text x="24" y="430" className="fo-t-title fo-t-accent-g">trend radar</text>
      <rect x="24" y="466" width="118" height="28" className="fo-node" shapeRendering="crispEdges" />
      <text x="38" y="483.5" className="fo-t-node">𝕏 live feed</text>
      <rect x="190" y="440" width="178" height="80" className="fo-box fo-grok-box" shapeRendering="crispEdges" />
      <text x="204" y="462" className="fo-t-title fo-t-accent-g">grok · x scouts</text>
      {[0, 1, 2].map((i) => (
        <rect
          key={`g${i}`}
          x={208 + i * 26} y="472" width="13" height="13"
          transform={`rotate(45 ${214.5 + i * 26} 478.5)`}
          className="fo-gsq" style={{ "--i": i }}
        />
      ))}
      <text x="204" y="508" className="fo-t-cap">trend briefs → router</text>

      {/* ── tier 1 group: always-on (hermes + direct cli) ── */}
      <rect x="458" y="24" width="294" height="268" className="fo-tier-group" />
      <text x="744" y="18" textAnchor="end" className="fo-t-cap" style={{ fill: "#5FBFB9" }}>tier 1 · always-on</text>

      {/* ── router ── */}
      <rect x="240" y="160" width="150" height="150" className="fo-box" shapeRendering="crispEdges" />
      <text x="254" y="184" className="fo-t-title" style={{ fill: "var(--fg-2)" }}>router</text>
      <text x="254" y="202" className="fo-t-cap">model gateway</text>
      <text x="254" y="232" className="fo-t-node" style={{ fontSize: 9 }}>free models first</text>
      <text x="254" y="248" className="fo-t-node" style={{ fontSize: 9 }}>escalate on demand</text>
      <text x="254" y="264" className="fo-t-node" style={{ fontSize: 9 }}>no metered api</text>
      <text x="254" y="292" className="fo-t-cap" style={{ fill: "var(--green)" }}>▸ routing</text>

      {/* ── hermes substrate ── */}
      <rect x="470" y="36" width="270" height="114" className="fo-box fo-hermes-box" shapeRendering="crispEdges" />
      <text x="484" y="58" className="fo-t-title fo-t-accent-h">hermes · substrate</text>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect
          key={`h${i}`}
          x={490 + i * 28} y="72" width="18" height="18"
          className="fo-hsq fo-hsq-core" style={{ "--i": i }}
          shapeRendering="crispEdges"
        />
      ))}
      <text x="490" y="112" className="fo-t-cap">always-on profiles</text>
      <text x="484" y="138" className="fo-t-cap" style={{ fill: "#5FBFB9" }}>outlives what it monitors</text>

      {/* ── direct cli agents ── */}
      <rect x="500" y="186" width="240" height="94" className="fo-box fo-cli-box" shapeRendering="crispEdges" />
      <text x="514" y="208" className="fo-t-title" style={{ fill: "var(--fg-2)" }}>direct agents · cli</text>
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={`c${i}`}
          x={516 + i * 28} y="218" width="16" height="16"
          className="fo-csq" style={{ "--i": i }}
          shapeRendering="crispEdges"
        />
      ))}
      <text x="640" y="230" className="fo-t-cap">build &amp; ship</text>
      <text x="514" y="262" className="fo-t-cap">claude code · codex sessions</text>

      {/* watch rail label */}
      <text x="490" y="306" className="fo-t-cap" style={{ fill: "#5FBFB9" }}>watches ▾</text>

      {/* ── openclaw breadth tier ── */}
      <text x="744" y="310" textAnchor="end" className="fo-t-cap" style={{ fill: "#F5A623" }}>tier 2 · breadth · restartable</text>
      <rect x="470" y="316" width="270" height="172" className="fo-box fo-oc-box" shapeRendering="crispEdges" />
      <text x="484" y="338" className="fo-t-title fo-t-accent-o fo-oc-title">openclaw · breadth fleet</text>
      <g>
        {ocDots.map((d) => {
          const stall = d.row === STALL_ROW;
          return (
            <rect
              key={d.i}
              x={d.x} y={d.y} width="10" height="10"
              className={stall ? "fo-dot fo-dot--stall" : "fo-dot"}
              style={{ "--i": dieOrder[d.i], "--ri": liteOrder[d.i], "--si": d.col }}
              shapeRendering="crispEdges"
            />
          );
        })}
      </g>
      <text x="484" y="448" className="fo-t-cap">workers · scouts · routers · dispatchers</text>
      <text x="484" y="466" className="fo-t-cap" style={{ fill: "var(--fg-5)" }}>hermes restarts stalls</text>

      {/* ── outputs ── */}
      <text x="822" y="38" className="fo-t-title">outputs</text>
      {OUTPUTS.map((name, i) => (
        <g key={name}>
          <rect x="822" y={OUT_YS[i] - 14} width="114" height="28" className="fo-node" shapeRendering="crispEdges" />
          <text x="836" y={OUT_YS[i] + 3.5} className="fo-t-node">{name} ▸</text>
        </g>
      ))}

      {/* packet layer — populated imperatively by the engine */}
      <g ref={packetLayerRef} />
    </svg>
  );
}

/* journey legs per phase. Each leg: laneId + packet color class.
   During a restart, Hermes picks up the stalled group's slack — more
   traffic flows to the always-on tier — but the breadth fleet keeps
   running the rest. Nothing fully stops. */
function pickJourney(phase) {
  const healing = phase === "blip" || phase === "healing";
  const inLane = `in${Math.floor(Math.random() * INPUTS.length)}`;
  const start = { lane: inLane, cls: "fo-pk-in" };
  const r = Math.random();
  if (healing) {
    /* hermes absorbs the stalled group's work; cli keeps building */
    if (r < 0.5) {
      return [start, { lane: "r2h", cls: "fo-pk-hermes" },
        { lane: Math.random() < 0.5 ? "h_reports" : "h_alerts", cls: "fo-pk-hermes" }];
    }
    if (r < 0.75) {
      return [start, { lane: "r2c", cls: "fo-pk-cli" },
        { lane: Math.random() < 0.5 ? "c_deploys" : "c_commits", cls: "fo-pk-out" }];
    }
    /* breadth still serving from the healthy groups */
    return [start, { lane: "r2o", cls: "fo-pk-breadth" },
      { lane: Math.random() < 0.5 ? "o_posts" : "o_replies", cls: "fo-pk-out" }];
  }
  if (r < 0.15) {
    return [start, { lane: "r2h", cls: "fo-pk-hermes" },
      { lane: Math.random() < 0.5 ? "h_reports" : "h_alerts", cls: "fo-pk-out" }];
  }
  if (r < 0.40) {
    return [start, { lane: "r2c", cls: "fo-pk-cli" },
      { lane: Math.random() < 0.5 ? "c_deploys" : "c_commits", cls: "fo-pk-out" }];
  }
  return [start, { lane: "r2o", cls: "fo-pk-breadth" },
    { lane: Math.random() < 0.5 ? "o_posts" : "o_replies", cls: "fo-pk-out" }];
}

/* grok trend journey: x → grok → router → breadth drafts a post (or the
   always-on tier while a worker group is healing). The full content
   pipeline in one packet. */
function pickGrokJourney(phase) {
  const healing = phase === "blip" || phase === "healing";
  const head = [
    { lane: "x2g", cls: "fo-pk-grok" },
    { lane: "g2r", cls: "fo-pk-grok" },
  ];
  if (healing && Math.random() < 0.5) {
    return [...head, { lane: "r2h", cls: "fo-pk-hermes" }, { lane: "h_reports", cls: "fo-pk-out" }];
  }
  return [...head, { lane: "r2o", cls: "fo-pk-breadth" }, { lane: "o_posts", cls: "fo-pk-out" }];
}

function FleetOps() {
  const reduced = usePrefersReducedMotionLocal();
  const [phase, setPhase] = useState("normal");
  const [replaying, setReplaying] = useState(false);
  const [statusMsg, setStatusMsg] = useState(PHASE_MSG.normal);
  const [typed, setTyped] = useState(PHASE_MSG.normal);
  const [tickIdx, setTickIdx] = useState(0);
  const [seen, setSeen] = useState(false);

  const phaseRef = useRef("normal");
  const sectionRef = useRef(null);
  const packetLayerRef = useRef(null);
  const pathEls = useRef({});
  const pathLens = useRef({});
  const packets = useRef([]);
  const rafId = useRef(0);
  const visible = useRef(true);
  const timers = useRef([]);
  const autoFired = useRef(false);

  useEffect(() => { injectFleetOpsStyles(); }, []);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  /* mark seen once — drives the box-head rule power-on sweep */
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { setSeen(true); obs.disconnect(); }
      });
    }, { threshold: 0.08 });
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const registerPath = (id, el) => {
    if (el) pathEls.current[id] = el;
  };

  /* status typewriter */
  useEffect(() => {
    if (reduced) { setTyped(statusMsg); return; }
    setTyped("");
    let i = 0;
    const iv = setInterval(() => {
      i += 2;
      setTyped(statusMsg.slice(0, i));
      if (i >= statusMsg.length) clearInterval(iv);
    }, 24);
    return () => clearInterval(iv);
  }, [statusMsg, reduced]);

  /* footer ticker rotation */
  useEffect(() => {
    if (reduced) return;
    const iv = setInterval(() => setTickIdx((i) => (i + 1) % TICK_LINES.length), 3200);
    return () => clearInterval(iv);
  }, [reduced]);

  /* ── packet engine ── */
  useEffect(() => {
    if (reduced || typeof document === "undefined") return;

    const inView = { current: true };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        inView.current = e.isIntersecting;
        visible.current = inView.current && !document.hidden;
      });
    }, { threshold: 0.05 });
    if (sectionRef.current) obs.observe(sectionRef.current);

    const onVis = () => { visible.current = inView.current && !document.hidden; };
    document.addEventListener("visibilitychange", onVis);

    const lenOf = (id) => {
      if (pathLens.current[id] == null) {
        const el = pathEls.current[id];
        pathLens.current[id] = el && el.getTotalLength ? el.getTotalLength() : 0;
      }
      return pathLens.current[id];
    };

    const spawnLeg = (journey, legIdx) => {
      const leg = journey[legIdx];
      const el = pathEls.current[leg.lane];
      const layer = packetLayerRef.current;
      if (!el || !layer) return;
      const node = document.createElementNS(SVG_NS, "rect");
      node.setAttribute("width", "5");
      node.setAttribute("height", "5");
      node.setAttribute("class", `fo-pk ${leg.cls}`);
      layer.appendChild(node);
      packets.current.push({
        node, pathEl: el, len: lenOf(leg.lane),
        t: 0, dur: 620 + Math.random() * 420,
        journey, legIdx,
      });
    };

    let last = performance.now();
    let nextSpawn = last + 400;
    let nextGrok = last + 1400;
    let nextAlert = 0;

    const frame = (now) => {
      rafId.current = requestAnimationFrame(frame);
      const dt = Math.min(64, now - last);
      last = now;
      if (document.hidden) return;

      const ph = phaseRef.current;

      /* spawn full journeys */
      if (visible.current && now >= nextSpawn && packets.current.length < 20) {
        spawnLeg(pickJourney(ph), 0);
        nextSpawn = now + 340 + Math.random() * 640;
      }
      /* grok trend journeys on their own cadence */
      if (visible.current && now >= nextGrok && packets.current.length < 22) {
        spawnLeg(pickGrokJourney(ph), 0);
        nextGrok = now + 2400 + Math.random() * 1800;
      }
      /* one quick alert pulse when the stall is first detected (blip) */
      if (visible.current && ph === "blip" && now >= nextAlert) {
        spawnLeg([{ lane: "h_alerts", cls: "fo-pk-alert" }], 0);
        nextAlert = now + 900 + Math.random() * 300;
      }

      /* advance packets */
      const alive = [];
      for (const p of packets.current) {
        p.t += dt / p.dur;
        if (p.t >= 1) {
          p.node.remove();
          if (p.legIdx + 1 < p.journey.length) spawnLeg(p.journey, p.legIdx + 1);
          continue;
        }
        if (p.len > 0) {
          const pt = p.pathEl.getPointAtLength(p.t * p.len);
          p.node.setAttribute("transform", `translate(${pt.x - 2.5}, ${pt.y - 2.5})`);
        }
        alive.push(p);
      }
      packets.current = alive;
    };
    rafId.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId.current);
      obs.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      packets.current.forEach((p) => p.node.remove());
      packets.current = [];
    };
  }, [reduced]);

  /* ── restart sequence ──
     A worker group stalls, Hermes detects + restarts it, and the fleet
     is back online. */
  const replay = () => {
    if (replaying || reduced) return;
    setReplaying(true);
    const seq = [
      ["blip", PHASE_MSG.blip, 0],
      ["healing", PHASE_MSG.healing, 1300],
      ["healed", PHASE_MSG.healed, 3400],
      ["normal", PHASE_MSG.post, 5200],
    ];
    seq.forEach(([ph, msg, at]) => {
      timers.current.push(setTimeout(() => {
        setPhase(ph);
        setStatusMsg(msg);
        if (ph === "normal") setReplaying(false);
      }, at));
    });
  };
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  /* ?replay=1 — scroll the board into view, then auto-fire the restart
     sequence. For clean screen recordings. */
  useEffect(() => {
    if (reduced || typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("replay") !== "1") return;
    const t = setTimeout(() => {
      if (sectionRef.current) sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
    timers.current.push(t);
  }, [reduced]);
  useEffect(() => {
    if (!seen || reduced || autoFired.current || typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("replay") !== "1") return;
    autoFired.current = true;
    const t = setTimeout(replay, 1100);
    timers.current.push(t);
  }, [seen, reduced]);

  const statusCls =
    phase === "blip" ? "fo-status-alert" :
    phase === "healing" ? "fo-status-principle" : "";

  return (
    <section data-section="fleetops" className={`section-pad${seen ? " kz-seen" : ""}`} ref={sectionRef} style={{ paddingTop: 28 }}>
      <div className="container">
        <div className="box-head">
          <div className="rule" />
          <span>fleet ops · live topology</span>
          <div className="rule right" />
        </div>

        <h2 className="h-section" style={{ marginBottom: 8 }}>
          Two tiers. One operator.
          <br />
          <span className="dim2" style={{ fontWeight: 600 }}>{">"} watch the work flow through the fleet.</span>
        </h2>

        <div className="win" style={{ marginTop: 24 }}>
          <div className="win-title">
            <div className="lights"><span className="r" /><span className="a" /><span className="g" /></div>
            <span className="fo-title-label">fleet-ops</span>
            <span className="meta" style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
              {!reduced && (
                <button className="fo-replay" onClick={replay} disabled={replaying} type="button">
                  {replaying ? "restarting…" : "▶ watch a worker restart"}
                </button>
              )}
            </span>
          </div>

          <div style={{ padding: "16px 16px 14px" }}>
            <div className="fo-scroll">
              <FleetBoard phase={phase} registerPath={registerPath} packetLayerRef={packetLayerRef} />
            </div>
            <div className="fo-drag-hint">← drag to pan the board →</div>

            <div className={`fo-status ${statusCls}`} aria-live="polite">
              <span className="fo-status-prompt">▸ </span>
              {typed}
              {!reduced && typed.length < statusMsg.length && <span className="cursor" style={{ height: 12, width: 7 }} />}
            </div>

            <div className="fo-legend">
              <span><i style={{ background: "#5FBFB9" }} />hermes · substrate</span>
              <span><i style={{ background: "var(--fg-2)" }} />direct cli · claude code + codex</span>
              <span><i style={{ background: "#F5A623" }} />openclaw · breadth</span>
              <span><i style={{ background: "var(--magenta, #c98ec0)" }} />grok · x scouts</span>
              <span><i style={{ background: "var(--green)" }} />shipped output</span>
              <span><i style={{ background: "var(--red)" }} />alert path</span>
            </div>
          </div>

          <div className="win-foot">
            <span className="fo-tick" key={tickIdx}>▸ {TICK_LINES[tickIdx]}</span>
            <span>openclaw · hermes · cli<span className="dim"> — </span><span style={{ color: "var(--green)" }}>fleet online</span></span>
          </div>
        </div>

        <p className="mt-16" style={{ fontSize: 12, color: "var(--fg-3)", maxWidth: "70ch", lineHeight: 1.6 }}>
          The design rule behind the split: <span style={{ color: "#5FBFB9" }}>a monitor must not depend on what it monitors.</span>{" "}
          Hermes watches the breadth fleet and restarts any worker that stalls.
          Full writeup: <a href="/post/agent-fleet-architecture" style={{ color: "var(--fg-2)", borderBottom: "1px dashed var(--line-strong)" }}>agent-fleet-architecture</a>.
        </p>

        {/* ReadyIQ off-ramp — the commercial exit from the proof artifact */}
        <div className="fo-readyiq-cta">
          <span className="fo-rq-q">
            Want this pattern inside your company? <b>ReadyIQ</b> deploys it — start with the free readiness scorecard.
          </span>
          <a href="https://www.readyiq.ai/book" target="_blank" rel="noreferrer">
            ▸ book an AI Audit
          </a>
        </div>
      </div>
    </section>
  );
}

Object.assign(window, { FleetOps });

export { FleetOps };
